'use client';

import React, { useState, useMemo } from 'react';
import {
    Calculator,
    Calendar,
    ChevronDown,
    Printer,
    FileDown,
    ArrowUpRight,
    TrendingUp,
    ShieldCheck,
    Wallet,
    CreditCard,
    ArrowRightLeft,
    AlertCircle,
    CheckCircle2
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

interface BalanceSheetItem {
    accountCode: string;
    accountName: string;
    amount: number;
}

interface BalanceSheetSection {
    title: string;
    items: BalanceSheetItem[];
}

// --- Mock Data ---

const MOCK_BALANCE_SHEET = {
    unit: 'PAUD Al-Azhar 1',
    date: '29 Februari 2024',
    assets: [
        {
            title: 'Aset Lancar', items: [
                { accountCode: '1-100', accountName: 'Kas Tunai', amount: 5000000 },
                { accountCode: '1-101', accountName: 'Bank BSI - Operasional', amount: 45000000 },
                { accountCode: '1-102', accountName: 'Bank Mandiri - Yayasan', amount: 150000000 },
                { accountCode: '1-201', accountName: 'Piutang SPP (Tangguhan)', amount: 12000000 }
            ]
        },
        {
            title: 'Aset Tetap', items: [
                { accountCode: '1-301', accountName: 'Inventaris Sekolah', amount: 85000000 },
                { accountCode: '1-302', accountName: 'Akumulasi Penyusutan', amount: -5000000 }
            ]
        }
    ],
    liabilities: [
        {
            title: 'Liabilitas Jangka Pendek', items: [
                { accountCode: '2-101', accountName: 'Hutang Gaji Staff', amount: 0 },
                { accountCode: '2-102', accountName: 'Hutang Operasional (Vendor)', amount: 2350000 }
            ]
        }
    ],
    equity: [
        {
            title: 'Ekuitas', items: [
                { accountCode: '3-101', accountName: 'Modal Awal Yayasan', amount: 200000000 },
                { accountCode: '3-201', accountName: 'Laba Ditahan', amount: 7500000 },
                { accountCode: '3-301', accountName: 'Laba Periode Berjalan', amount: 8150000 }
            ]
        }
    ]
};

export default function LaporanNeracaPage() {
    // --- Calculations ---
    const financialPosition = useMemo(() => {
        const totalAssets = MOCK_BALANCE_SHEET.assets.reduce((sum, section) =>
            sum + section.items.reduce((secSum, item) => secSum + item.amount, 0), 0);

        const totalLiabilities = MOCK_BALANCE_SHEET.liabilities.reduce((sum, section) =>
            sum + section.items.reduce((secSum, item) => secSum + item.amount, 0), 0);

        const totalEquity = MOCK_BALANCE_SHEET.equity.reduce((sum, section) =>
            sum + section.items.reduce((secSum, item) => secSum + item.amount, 0), 0);

        const isBalanced = Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01;

        return { totalAssets, totalLiabilities, totalEquity, isBalanced };
    }, []);

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Laporan Keuangan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Neraca (Balance Sheet)</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <Calculator className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Neraca Keuangan</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <ShieldCheck className="w-3.5 h-3.5 text-success" /> Posisi Aset • Liabilitas • Ekuitas
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/30 transition-all shadow-sm group">
                            <FileDown className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" /> Export Excel
                        </button>
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                            <Printer className="w-4 h-4" /> Export PDF
                        </button>
                    </div>
                </div>
            </section>

            {/* Filter Section */}
            <div className="bg-surface border border-border-main p-6 rounded-3xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-2 flex-1 md:max-w-[280px]">
                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Posisi Per Tanggal (Snapshot)</label>
                    <button className="w-full flex items-center justify-between px-5 py-3.5 bg-background border border-border-main rounded-2xl text-[11px] font-bold text-foreground">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-4 h-4 text-primary" />
                            {MOCK_BALANCE_SHEET.date}
                        </div>
                        <ChevronDown className="w-4 h-4 text-muted" />
                    </button>
                </div>

                <div className={`px-6 py-3.5 rounded-2xl border flex items-center gap-4 transition-all ${financialPosition.isBalanced ? 'bg-success-soft/20 border-success/10 text-success' : 'bg-error-soft/20 border-error/10 text-error animate-pulse'}`}>
                    {financialPosition.isBalanced ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                    <div className="space-y-0.5">
                        <p className="text-[10px] font-black uppercase tracking-widest">Status Neraca</p>
                        <p className="text-xs font-bold uppercase tracking-tighter">{financialPosition.isBalanced ? 'Balanced / Seimbang' : 'Unbalanced / Tidak Seimbang'}</p>
                    </div>
                </div>
            </div>

            {/* Main Report Table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">

                {/* Left Side: ASSETS */}
                <div className="space-y-6">
                    <div className="flex items-center gap-3 px-4">
                        <div className="p-2 bg-primary-soft text-primary rounded-xl"><Wallet className="w-5 h-5" /></div>
                        <h3 className="text-xl font-black text-foreground uppercase tracking-tight">AKTIVA (ASET)</h3>
                    </div>

                    <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-sm">
                        {MOCK_BALANCE_SHEET.assets.map((section, sIdx) => (
                            <div key={sIdx}>
                                <div className="bg-background/50 px-8 py-4 border-b border-border-main">
                                    <p className="text-[10px] font-black text-muted uppercase tracking-[0.2em]">{section.title}</p>
                                </div>
                                <div className="p-8 pb-4 space-y-4">
                                    {section.items.map((item, iIdx) => (
                                        <div key={iIdx} className="flex justify-between group">
                                            <div className="space-y-0.5">
                                                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-all">{item.accountName}</p>
                                                <p className="text-[10px] font-medium text-muted uppercase font-mono">{item.accountCode}</p>
                                            </div>
                                            <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                        <div className="bg-primary-soft/30 p-8 border-t border-primary/10 flex items-center justify-between">
                            <p className="text-[11px] font-black text-primary uppercase tracking-[0.3em]">Total Aktiva</p>
                            <p className="text-2xl font-black text-primary tabular-nums">{formatCurrency(financialPosition.totalAssets)}</p>
                        </div>
                    </div>
                </div>

                {/* Right Side: LIABILITIES & EQUITY */}
                <div className="space-y-8">

                    {/* LIABILITIES */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 px-4">
                            <div className="p-2 bg-error-soft text-error rounded-xl"><ArrowRightLeft className="w-5 h-5" /></div>
                            <h3 className="text-xl font-black text-foreground uppercase tracking-tight">PASIVA (KEWAJIBAN)</h3>
                        </div>
                        <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-sm">
                            {MOCK_BALANCE_SHEET.liabilities.map((section, sIdx) => (
                                <div key={sIdx}>
                                    <div className="bg-background/50 px-8 py-4 border-b border-border-main">
                                        <p className="text-[10px] font-black text-muted uppercase tracking-[0.2em]">{section.title}</p>
                                    </div>
                                    <div className="p-8 pb-4 space-y-4">
                                        {section.items.map((item, iIdx) => (
                                            <div key={iIdx} className="flex justify-between group">
                                                <div className="space-y-0.5">
                                                    <p className="text-sm font-bold text-foreground group-hover:text-error transition-all">{item.accountName}</p>
                                                    <p className="text-[10px] font-medium text-muted uppercase font-mono">{item.accountCode}</p>
                                                </div>
                                                <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <div className="bg-error-soft/30 p-8 border-t border-error/10 flex items-center justify-between">
                                <p className="text-[11px] font-black text-error uppercase tracking-[0.3em]">Total Kewajiban</p>
                                <p className="text-xl font-black text-error tabular-nums">{formatCurrency(financialPosition.totalLiabilities)}</p>
                            </div>
                        </div>
                    </div>

                    {/* EQUITY */}
                    <div className="space-y-6">
                        <div className="flex items-center gap-3 px-4">
                            <div className="p-2 bg-success-soft text-success rounded-xl"><CreditCard className="w-5 h-5" /></div>
                            <h3 className="text-xl font-black text-foreground uppercase tracking-tight">EKUITAS (MODAL)</h3>
                        </div>
                        <div className="bg-surface border border-border-main rounded-4xl overflow-hidden shadow-sm">
                            {MOCK_BALANCE_SHEET.equity.map((section, sIdx) => (
                                <div key={sIdx}>
                                    <div className="bg-background/50 px-8 py-4 border-b border-border-main">
                                        <p className="text-[10px] font-black text-muted uppercase tracking-[0.2em]">{section.title}</p>
                                    </div>
                                    <div className="p-8 pb-4 space-y-4">
                                        {section.items.map((item, iIdx) => (
                                            <div key={iIdx} className="flex justify-between group">
                                                <div className="space-y-0.5">
                                                    <p className="text-sm font-bold text-foreground group-hover:text-success transition-all">{item.accountName}</p>
                                                    <p className="text-[10px] font-medium text-muted uppercase font-mono">{item.accountCode}</p>
                                                </div>
                                                <p className="text-sm font-black text-foreground tabular-nums">{formatCurrency(item.amount)}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <div className="bg-success-soft/30 p-8 border-t border-success/10 flex items-center justify-between">
                                <p className="text-[11px] font-black text-success uppercase tracking-[0.3em]">Total Ekuitas</p>
                                <p className="text-xl font-black text-success tabular-nums">{formatCurrency(financialPosition.totalEquity)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Pasiva Total Summary */}
                    <div className="p-8 bg-primary rounded-[32px] text-white flex items-center justify-between shadow-xl shadow-primary/20 transition-all hover:scale-[1.02]">
                        <div className="space-y-1">
                            <p className="text-[10px] font-black text-primary-soft uppercase tracking-[0.3em]">Total Pasiva</p>
                            <p className="text-[10px] font-medium text-primary-soft">(Liabilitas + Ekuitas)</p>
                        </div>
                        <p className="text-2xl font-black tabular-nums">{formatCurrency(financialPosition.totalLiabilities + financialPosition.totalEquity)}</p>
                    </div>

                </div>

            </div>

            {/* Bottom Analysis Footer */}
            <div className="flex flex-col md:flex-row items-center justify-between p-10 bg-surface border border-border-main rounded-[40px] shadow-sm gap-8 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
                <div className="flex items-center gap-6 relative z-10">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-all ${financialPosition.isBalanced ? 'bg-success text-white' : 'bg-error text-white h-20 w-20'}`}>
                        {financialPosition.isBalanced ? <TrendingUp className="w-7 h-7" /> : <AlertCircle className="w-10 h-10" />}
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-lg font-black text-foreground uppercase tracking-tight">Analisis Posisi Keuangan</h4>
                        <p className="text-xs font-medium text-secondary leading-relaxed max-w-lg">
                            {financialPosition.isBalanced
                                ? 'Laporan neraca seimbang. Struktur aset sekolah didominasi oleh dana tunai di bank, menunjukkan likuiditas yang sangat sehat untuk operasional tahun ajaran ini.'
                                : 'PERINGATAN: Laporan neraca tidak seimbang. Terdapat selisih pencatatan antara Aktiva dan Pasiva yang memerlukan rekonsiliasi jurnal segera.'}
                        </p>
                    </div>
                </div>
                <div className="flex gap-4">
                    <button className="px-8 py-4 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/50 transition-all shadow-sm">Bandingkan Snapshot</button>
                    <button className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">Download Full Report</button>
                </div>
            </div>
        </main>
    );
}
